import { Alert, Box, Button, Paper, Typography } from '@mui/material';
import { RefreshRounded, HomeTwoTone, ReportProblemOutlined } from '@mui/icons-material';

export type ErrorPageMode = 'generic' | 'not-found' | 'legacy';

type ErrorPageProps = {
    mode?: ErrorPageMode;
    title?: string;
    description?: string;
    showReload?: boolean;
};

const ERROR_CONTENT: Record<ErrorPageMode, { code: string; title: string; description: string }> = {
    generic: {
        code: '500',
        title: 'Something went wrong',
        description: 'An unexpected error occurred while loading this page. Please reload and try again.'
    },
    'not-found': {
        code: '404',
        title: 'Page not found',
        description: 'The page may have moved, been removed, or the URL might be incorrect.'
    },
    legacy: {
        code: 'Error',
        title: 'Something is wrong',
        description: 'The page you are looking for was moved, removed, renamed, or might never exist.'
    }
};

const Error = ({ mode = 'legacy', title, description, showReload = true }: ErrorPageProps) => {
    const content = ERROR_CONTENT[mode];
    const resolvedTitle = title ?? content.title;
    const resolvedDescription = description ?? content.description;
    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: { xs: 2, sm: 3 }
            }}
        >
            <Paper
                elevation={3}
                sx={{
                    width: '100%',
                    maxWidth: 720,
                    borderRadius: 3,
                    p: { xs: 3, sm: 5 },
                    textAlign: 'center'
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2.5 }}>
                    <ReportProblemOutlined color="warning" sx={{ fontSize: { xs: 52, sm: 62 } }} />
                    <Typography variant="h2" sx={{ fontSize: { xs: '2rem', sm: '2.75rem' }, lineHeight: 1.1 }}>
                        {content.code}
                    </Typography>
                    <Typography variant="h3">{resolvedTitle}</Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 520 }}>
                        {resolvedDescription}
                    </Typography>
                    <Alert severity="info" sx={{ textAlign: 'left', width: '100%' }}>
                        If this keeps happening, contact support and include the page URL.
                    </Alert>
                    <Box
                        sx={{
                            width: { xs: '100%', sm: 'auto' },
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: 1.5
                        }}
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            size="medium"
                            startIcon={<HomeTwoTone />}
                            onClick={() => globalThis.location.assign('/')}
                            sx={{ width: 200 }}
                        >
                            Go Home
                        </Button>
                        {showReload && (
                            <Button
                                variant="outlined"
                                color="primary"
                                size="medium"
                                startIcon={<RefreshRounded />}
                                onClick={() => globalThis.location.reload()}
                                sx={{ width: 200 }}
                            >
                                Reload
                            </Button>
                        )}
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
};

export default Error;
